import { createContext, useContext, useState, ReactNode } from 'react';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { RateType } from '../OfferBuilderContext';

type SetRateModalMode = 'buyer' | 'initial' | 'summary' | 'takerMarket';

interface SetRateModalState {
  isOpen: boolean;
  buyer?: BuyerGroupWithStats;
  initialRateType?: RateType;
  mode?: SetRateModalMode;
  onNext?: () => void;
  takerMarket?: TakerMarket;
}

interface SetRateModalConfig {
  mode: SetRateModalMode;
  buyer?: BuyerGroupWithStats;
  initialRateType?: RateType;
  onNext?: () => void;
  takerMarket?: TakerMarket;
}

interface SetRateModalContextValue {
  closeModal: () => void;
  openModal: (config: SetRateModalConfig) => void;
  state: SetRateModalState;
}

export const SetRateModalContext = createContext<SetRateModalContextValue | undefined>(undefined);

export const useSetRateModal = () => {
  const context = useContext(SetRateModalContext);

  if (!context) {
    throw new Error('useSetRateModal must be used within a SetRateModalProvider');
  }

  return context;
};

interface SetRateModalProviderProps {
  children: ReactNode;
}

export const SetRateModalProvider = ({ children }: SetRateModalProviderProps) => {
  const [state, setState] = useState<SetRateModalState>({ isOpen: false });

  const openModal = ({ buyer, initialRateType, mode, onNext, takerMarket }: SetRateModalConfig) => {
    setState({
      buyer,
      initialRateType,
      isOpen: true,
      mode,
      onNext,
      takerMarket,
    });
  };

  const closeModal = () => {
    setState({
      buyer: undefined,
      initialRateType: undefined,
      isOpen: false,
      mode: undefined,
      onNext: undefined,
      takerMarket: undefined,
    });
  };

  return (
    <SetRateModalContext.Provider value={{ closeModal, openModal, state }}>{children}</SetRateModalContext.Provider>
  );
};
